// models.ts - Versione 2026.1.16

export type QuestionnaireType = 'SURVEY' | 'CHECKLIST' | 'FILE_CHECKLIST';

/**
 * Tipi di domanda allineati alla specifica OpenAPI 3.0.3 del 2026.
 */
export type QuestionType = 'SINGLESELECT' | 'MULTISELECT' | 'BOOLEAN' | 'NUMBER' | 'TEXT' | 'DATE';

/**
 * Opzione singola per domande a scelta multipla o singola.
 */
export interface QuestionOption {
  id: string;
  position: number;
  text: string;
  triggersAlert?: boolean;
  requiresComment?: boolean;
  active?: boolean; // false = cancellazione logica (opzione rimossa ma con risposte storiche)
}

/**
 * Rappresentazione di una domanda completa.
 */
export interface Question {
  id: string;
  text: string;
  tooltip?: string;
  position: number;
  mandatory: boolean;
  type: QuestionType;
  options?: QuestionOption[];
}

/**
 * Richiesta per la creazione o l'aggiornamento di una domanda.
 */
export interface CreateQuestionRequest {
  text: string;
  tooltip?: string;
  type: QuestionType;
  options?: QuestionOption[];
  position: number;
  mandatory: boolean;
}

/**
 * Modello principale del Questionario.
 */
export interface Questionnaire {
  id: string;
  name: string;
  tooltip?: string;
  trusteeTypes: string[];
  type: QuestionnaireType;
  validFrom: string; // Formato ISO YYYY-MM-DD
  validTo: string; // Formato ISO YYYY-MM-DD
  questions: Question[];
}

/**
 * Modello principale del Questionario.
 * Aggiornato per supportare status (DRAFT/FILLED) invece di isCompleted boolean
 */
export interface QuestionnaireStatus {
  questionnaireId: string;
  name: string;
  version: number;
  answerId: string | null;
  instanceId?: string | null;
  status: "DRAFT" | "FILLED" | null;
}

/**
 * Richiesta per la creazione di un nuovo questionario.
 */
export interface CreateQuestionnaireRequest {
  name: string;
  tooltip?: string;
  type: QuestionnaireType;
  trusteeTypes: string[];
  validFrom: string;
  validTo: string;
  version: number; // Campo versione
  modifiedBy?: string;
  questions: CreateQuestionRequest[];
}

/**
 * Struttura di sottomissione risposte corretta per il backend.
 * Il server richiede una mappa (dizionario) dove la chiave è l'ID della domanda.
 */
export interface QuestionnaireAnswerResponse {
  questionnaireId: string;
  instanceId: string;
  professionalId: string;
  answerData: {
    [questionId: string]: string | number | boolean | any[] | null;
  };
  fileAttachments?: {
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ResponseSubmission {
  questionnaireId: string;
  instanceId: string | null; // null = nuovo record (INSERT), UUID = aggiorna esistente (UPDATE)
  professionalId: string;
  trusteeType: string; // ID del fiduciario che sta compilando il questionario
  status: "DRAFT" | "FILLED"; // ⚠️ OBBLIGATORIO: DRAFT = bozza, FILLED = completato
  answers: {
    [questionId: string]: string | number | boolean | any[] | null;
  };
}

/**
 * Risposta standard di successo per le operazioni di sottomissione.
 */
export interface SubmitResponseSuccess {
  status: string;
  message?: string;
}

/**
 * Rappresenta una richiesta di upload file per una checklist documentale.
 */
export interface FileUploadRequest {
  text: string;
  position: number;
  fileSystemPath?: string;
  templateFile?: string;
}

/**
 * Rappresenta una checklist documentale per contratto.
 */
export interface ContractChecklist {
  name: string;
  trusteeTypes: string[];
  validFrom: string; // YYYY-MM-DD
  validTo: string; // YYYY-MM-DD
  fileUploadRequests: FileUploadRequest[];
  modifiedBy: string;
}

export interface Checklist {
  id: string;
  name: string;
  type: QuestionnaireType;
  trusteeTypes: string[];
  validFrom: string;
  validTo: string;
  version: number;
  modifiedBy: string;
  createdAt: string;
  updatedAt: string;
  fileUploadRequests: FileUploadRequest[];
}

/**
 * Professional pre-registration request.
 */
export interface ProfessionalPreRegistration {
  type: 'AUTO_EXPERT' | 'RE_EXPERT' | 'DOCTOR' | 'LAWYER' | 'INVESTIGATOR' | 'BODYSHOP';
  bodyShopType?: 'STANDARD' | 'ARS' | 'RENAULT_GROUP_RCI' | 'AXA_DEUTSCHE_BANK';
  legalOrPhysical?: 'FISICA' | 'GIURIDICA';
  companyType?: string;
  businessName?: string; // Ragione Sociale for GIURIDICA
  firstName: string;
  lastName: string;
  fiscalCode: string;
  vatNumber: string;
  registrationEmail: string;
  confirmRegistrationEmail?: string;
  /** Se true, bypassa il controllo duplicati su fiscalCode e vatNumber */
  forceSave?: boolean;
  parentProfessionalId?: string;
}

/**
 * Alert notification model.
 */
export interface Alert {
  id: string;
  professionalId: string;
  questionnaireId: string;
  questionId: string;
  questionOptionId: string;
  resolved: boolean;
  disabled: boolean;
  alertMessage: string;
  triggeredAt: string;
  updatedAt: string;
  // TODO: campi leggibili da aggiungere nel BE (pal-dmn-core)
  professionalName?: string;
  questionnaireName?: string;
  questionText?: string;
  optionText?: string;
  trusteeType?: string;
}

export type AlertStatus = 'UNREAD' | 'IN_PROGRESS' | 'RESOLVED';

export function getAlertStatus(alert: Alert): AlertStatus {
  if (alert.resolved) return 'RESOLVED';
  if (alert.disabled) return 'IN_PROGRESS';
  return 'UNREAD';
}

/**
 * VRF Questionnaire - Sub-Responsible model
 */
export interface VRFSubResponsible {
  id?: number;
  companyName: string;
  vatNumber: string;
  headquartersCountry: string;
  operatingProvince: string;
  operatingRegion: string;
  headquartersPostalCode: string;
  operatingMunicipality: string;
  operatingStreetType: string;
  operatingAddress: string;
  operatingStreetNumber: string;
  dataProcessingLocation: 'EU' | 'EXTRA_EU' | '';
  specificLocationLegalBasis: string;
  extraEuTransferSafeguards: string;
  dataTypesProcessed: string[];
  dataTypesProcessedOther?: string;
  processesDescription: string;
}

/**
 * VRF Questionnaire model (PATCH request body)
 */

export interface VRFQuestionnaire {
  responsibleCompanyName?: string;
  dpoFirstName?: string;
  dpoLastName?: string;
  dpoEmail?: string;
  dataRecipientsCategories: string;
  dataRecipientsCategoriesOther?: string;
  dataTransferOutsideEu?: boolean | null;
  gdprComplianceMeasures?: boolean | null;
  dataDeletionMethod: string;
  itSystemsUsed: string;
  itSystemsOther: string;
  credentialsAccess: boolean;
  softwareUpdates: boolean;
  passwordUpdates: boolean;
  antivirusFirewall: boolean;
  periodicBackup: boolean;
  staffTraining: boolean;
  authorizedPersonnelList: boolean;
  hasSubResponsibles: boolean;
  subResponsibles: VRFSubResponsible[];
  systemAdministratorsInfo: string;
  cloudStorageUsed: boolean;
  cloudType: string;
}

/**
 * VRF Questionnaire response (GET response body)
 */
export interface VRFQuestionnaireResponse extends VRFQuestionnaire {
  id: number;
  professionalId: string;
  status: 'DRAFT' | 'FILLED';
  createdAt: string;
  updatedAt: string;
}


export interface ApiResponse<T> {
  status: boolean;
  message: string;
  data: T;
}

export enum StepStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED'
}

export interface QuestionnaireFilechecklistStepResponse {
  id: string;
  professionalId: string;
  questionnaireId: string;
  stepStatus: StepStatus;
  stepComments: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}
